import { useState, useCallback, useMemo } from 'react'
import type { PaginationState, PaginationActions } from '@/shared/types/pagination'

/**
 * Hook for offset-based pagination.
 *
 * @param pageSize — items per page
 * @param total — total item count from API response (undefined while loading)
 * @returns Combined state and actions
 */
export function usePagination(
  pageSize: number,
  total: number | undefined,
): PaginationState & PaginationActions {
  const [page, setPage] = useState(0)

  const totalPages = useMemo(
    () => (total !== undefined ? Math.ceil(total / pageSize) : 0),
    [total, pageSize],
  )

  const nextPage = useCallback(() => {
    setPage((p) => p + 1)
  }, [])

  const prevPage = useCallback(() => {
    setPage((p) => Math.max(0, p - 1))
  }, [])

  const goToPage = useCallback((p: number) => {
    setPage(p)
  }, [])

  const reset = useCallback(() => {
    setPage(0)
  }, [])

  return {
    page,
    pageSize,
    offset: page * pageSize,
    total,
    totalPages,
    nextPage,
    prevPage,
    goToPage,
    reset,
  }
}
